// Zugriff auf das Statistik-Backend (/api, auf dem Server hinter Caddy).

export type GetIdToken = () => Promise<string>;

export interface StatsEvent {
  id: number;
  ts: string;
  userEmail: string;
  userName: string;
  sourceType: "sharepoint" | "local" | "single";
  objectName: string;
  images: number;
  titleImages: number;
  errors: number;
  durationSeconds: number;
  inputBytes: number;
  outputBytes: number;
  appBuild: string;
}

export type AiRollout = "admins" | "all" | "off";

export interface StatsSettings {
  minutesPerImage: number;
  updatedBy: string;
  updatedAt: string;
  aiKeyExpires: string; // JJJJ-MM-TT
  aiRollout: AiRollout;
}

export interface AiStatus {
  hasKey: boolean;
  model: string;
  keyExpires: string;
  rollout: AiRollout;
  imagesThisMonth: number;
  lastOk: string;
  lastError: { ts: string; code: string; detail: string; message: string } | null;
  providers?: Array<{ provider: string; model: string; status: string }>;
}

export interface Me {
  email: string;
  name: string;
  isAdmin: boolean;
  aiEnabled: boolean;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code = "",
  ) {
    super(message);
  }
}

export async function api<T>(getIdToken: GetIdToken, path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${await getIdToken()}`,
      "Content-Type": "application/json",
      ...init.headers,
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const b = body as { error?: string; code?: string };
    throw new ApiError(res.status, b.error ?? `Fehler ${res.status}`, b.code ?? "");
  }
  return body as T;
}

export const fetchMe = (t: GetIdToken) => api<Me>(t, "/me");
export const fetchEvents = (t: GetIdToken) => api<{ events: StatsEvent[] }>(t, "/admin/events").then((r) => r.events);
export const fetchSettings = (t: GetIdToken) => api<StatsSettings>(t, "/admin/settings");
export const saveSettings = (
  t: GetIdToken,
  patch: Partial<Pick<StatsSettings, "minutesPerImage" | "aiKeyExpires" | "aiRollout">>,
) => api<StatsSettings>(t, "/admin/settings", { method: "PUT", body: JSON.stringify(patch) });
export const saveMinutesPerImage = (t: GetIdToken, minutesPerImage: number) => saveSettings(t, { minutesPerImage });
export const fetchAiStatus = (t: GetIdToken) => api<AiStatus>(t, "/admin/ai");
export const suggestRoom = (t: GetIdToken, image: string) =>
  api<{ room: string }>(t, "/rooms/suggest", { method: "POST", body: JSON.stringify({ image }) }).then((r) => r.room);
export const deleteUserEvents = (t: GetIdToken, email: string) =>
  api<{ deleted: number }>(t, `/admin/users?email=${encodeURIComponent(email)}`, { method: "DELETE" });
